import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useUserRole } from "@/hooks/useUserRole";
import { usePartnerRole } from "@/hooks/usePartnerRole";

export type OrgRole = "proprietario" | "gerente" | "vendedor" | "partner";

export function useCurrentUserRole() {
  const { currentOrgId, currentOrg, isLoading: orgLoading } = useOrganization();
  const { isAdmin, isLoading: adminLoading } = useUserRole();
  const { isPartnerAdmin, partnerId, isLoading: partnerLoading } = usePartnerRole();

  const { data: role, isLoading: roleLoading } = useQuery({
    queryKey: ["current-user-role", currentOrgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !currentOrgId) return null;

      const { data } = await supabase
        .from("org_members")
        .select("role")
        .eq("org_id", currentOrgId)
        .eq("user_id", user.id)
        .single();

      return (data?.role as OrgRole) || null;
    },
    enabled: !!currentOrgId,
  });

  // Loading combinado: considera tanto o carregamento da org quanto do role
  const isLoading = orgLoading || roleLoading || adminLoading || partnerLoading;

  // ========== VERIFICAÇÃO DE ACESSO ELEVADO ==========
  // Admin tem acesso total a todas as organizações
  // Partner tem acesso total às organizações do seu partner
  const isPartnerOfOrg = isPartnerAdmin && currentOrg?.partner_id === partnerId;
  const hasElevatedAccess = isAdmin || isPartnerOfOrg;

  // Permissões só são calculadas quando role está definido
  const isProprietario = role === "proprietario";
  const isGerente = role === "gerente";
  const isVendedor = role === "vendedor";

  // ========== PERMISSÕES COM SUPORTE A ADMIN/PARTNER ==========
  // Admin e Partner têm as mesmas permissões de Proprietário

  return {
    role,
    isLoading,
    isProprietario,
    isGerente,
    isVendedor,
    
    // Flags de acesso elevado
    isAdmin,
    isPartnerAdmin,
    isPartnerOfOrg,
    hasElevatedAccess,

    // ========== GESTÃO DE EQUIPE E ORGANIZAÇÃO ==========
    canManageTeam: hasElevatedAccess || isProprietario,
    canAccessOrgSettings: hasElevatedAccess || isProprietario,
    canManagePipelines: hasElevatedAccess || isProprietario,
    canManageStages: hasElevatedAccess || isProprietario || isGerente,

    // ========== CONFIGURAÇÕES (LEADS) ==========
    // Origens de lead são configuração da organização (restrito a gerente/proprietário + acesso elevado)
    canManageLeadOrigins: hasElevatedAccess || isProprietario || isGerente,
    // Serviços/produtos seguem a mesma lógica de origens
    canManageServices: hasElevatedAccess || isProprietario || isGerente,
    
    // ========== GESTÃO DE LEADS ==========
    canDeleteLeads: hasElevatedAccess || isProprietario || isGerente,
    canBulkDelete: hasElevatedAccess || isProprietario,
    canImportLeads: hasElevatedAccess || isProprietario || isGerente,
    canExportLeads: hasElevatedAccess || isProprietario || isGerente,
    
    // ========== WHATSAPP ==========
    canConfigureWhatsApp: hasElevatedAccess || isProprietario || isGerente,
    canDisconnectWhatsApp: hasElevatedAccess || isProprietario || isGerente,
    canAccessWhatsAppChat: true, // Todos podem acessar o chat
    
    // ========== METAS ==========
    canConfigureGoals: hasElevatedAccess || isProprietario || isGerente,
    canViewGoals: true, // Todos podem visualizar
    
    // ========== EMAIL MARKETING ==========
    canAccessEmailMarketing: hasElevatedAccess || isProprietario || isGerente,
  };
}
